/**
 * DNAColumn (Nano-Metadata Table)
 *
 * ✅ ENTERPRISE V2: User-Friendly Display (NO jargon!)
 *
 * RULES:
 *   1. NO "chunks" or "vectors" shown to users
 *   2. Show meaningful progress stages during processing
 *   3. Correct plurality ("1 Page" vs "2 Pages")
 *   4. Only show searchable pages AFTER processing completes
 */

import React from 'react';

import {
  FileText,
  Image,
  FileSpreadsheet,
  FileType,
  Loader2,
  CheckCircle,
  AlertCircle,
} from 'lucide-react';
import '@/components/smart-notebook/SmartTable/SmartTable.css';

// File type icons
const FILE_ICONS = {
  pdf: FileText,
  word: FileText,
  excel: FileSpreadsheet,
  powerpoint: FileType,
  image: Image,
  text: FileText,
  document: FileText,
};

export default function DNAColumn({ row }) {
  const baseType = row?.basic_info?.base_type || 'document';
  const isLoading = row?.isLoading === true;
  const isReady = row?.isReady === true || row?.status === 'ready';
  const hasError = row?.hasError === true || row?.status === 'error';

  const FileIcon = FILE_ICONS[baseType] || FileText;

  // Page info
  const pageCount = row?.document_info?.pageCount || row?.details?.page_count || null;
  const hasTextLayer = row?.document_info?.hasTextLayer;
  const ocrRequired = row?.document_info?.ocrRequired;

  // User-facing display from row
  const dnaDisplay = row?.user_display?.dna || '';
  const searchableDisplay = row?.user_display?.searchable || '';
  const processingStage = row?.user_display?.stage || row?.processingStage || '';

  // ═══════════════════════════════════════════════════════════════════════════
  // ERROR STATE
  // ═══════════════════════════════════════════════════════════════════════════
  if (hasError) {
    return (
      <div className="dna-content dna-error">
        <div className="dna-row" title={row?.statusReason || 'Processing failed'}>
          <span className="dna-icon">❌</span>
          <span className="dna-value dna-error-text">Processing Failed</span>
        </div>
        {row?.statusReason && (
          <div className="dna-row dna-sub">
            <span className="dna-value dna-muted">{row.statusReason}</span>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LOADING STATE (user-friendly, no jargon)
  // ═══════════════════════════════════════════════════════════════════════════
  if (isLoading) {
    return (
      <div className="dna-content dna-loading">
        {/* Page count if available */}
        {pageCount && (
          <div
            className="dna-row"
            title={`Document has ${pageCount} page${pageCount === 1 ? '' : 's'}`}
          >
            <span className="dna-icon">📄</span>
            <span className="dna-value">
              {pageCount} {pageCount === 1 ? 'Page' : 'Pages'}
            </span>
          </div>
        )}

        {/* Processing stage (user-friendly) */}
        <div className="dna-row dna-processing" title={processingStage}>
          <Loader2 className="dna-icon dna-spinner" size={12} />
          <span className="dna-value dna-animated">{processingStage || 'Processing...'}</span>
        </div>

        {/* OCR indicator if needed */}
        {ocrRequired && baseType === 'pdf' && (
          <div className="dna-row dna-sub" title="This document requires text extraction">
            <span className="dna-icon">🔍</span>
            <span className="dna-value dna-muted">Needs text extraction</span>
          </div>
        )}

        {/* Searchable progress */}
        {pageCount && (
          <div className="dna-row dna-sub" title="Pages that can be searched by AI">
            <span className="dna-icon">○</span>
            <span className="dna-value dna-muted">
              0 / {pageCount} {pageCount === 1 ? 'page' : 'pages'} searchable
            </span>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // READY STATE
  // ═══════════════════════════════════════════════════════════════════════════
  if (isReady) {
    const healthTier = row?.quick_health?.health_tier || 'good';
    const isExcellent = healthTier === 'excellent';
    const isPoor = healthTier === 'poor' || healthTier === 'critical';

    return (
      <div className="dna-content dna-ready">
        {/* Page count */}
        {pageCount && (
          <div
            className="dna-row"
            title={`Document has ${pageCount} page${pageCount === 1 ? '' : 's'}`}
          >
            <span className="dna-icon">📄</span>
            <span className="dna-value">
              {pageCount} {pageCount === 1 ? 'Page' : 'Pages'}
            </span>
          </div>
        )}

        {/* Text layer status for PDFs */}
        {baseType === 'pdf' && hasTextLayer !== null && (
          <div
            className="dna-row"
            title={
              hasTextLayer ? 'Text can be selected and searched' : 'Text was extracted via OCR'
            }
          >
            <span className="dna-icon">{hasTextLayer ? '✓' : '🔍'}</span>
            <span className="dna-value">{hasTextLayer ? 'Searchable PDF' : 'OCR Processed'}</span>
          </div>
        )}

        {/* Divider */}
        <div className="dna-divider" />

        {/* Index status (user-friendly - NO chunks/vectors!) */}
        <div className="dna-row" title="Document is indexed and ready for AI queries">
          <CheckCircle className="dna-icon dna-success" size={12} />
          <span className="dna-value">Indexed for AI</span>
        </div>

        {/* Searchable pages */}
        {pageCount && (
          <div className="dna-row dna-sub" title="All pages are searchable by AI">
            <span className="dna-icon">●</span>
            <span className="dna-value dna-success-text">
              {pageCount} / {pageCount} {pageCount === 1 ? 'page' : 'pages'} searchable
            </span>
          </div>
        )}

        {/* Quality warning if poor */}
        {isPoor && (
          <div className="dna-row dna-warning" title="Document quality may affect AI accuracy">
            <AlertCircle className="dna-icon dna-warn" size={12} />
            <span className="dna-value dna-warn-text">Low quality detected</span>
          </div>
        )}
      </div>
    );
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // DEFAULT/QUEUED STATE
  // ═══════════════════════════════════════════════════════════════════════════
  return (
    <div className="dna-content dna-queued">
      {pageCount && (
        <div
          className="dna-row"
          title={`Document has ${pageCount} page${pageCount === 1 ? '' : 's'}`}
        >
          <span className="dna-icon">📄</span>
          <span className="dna-value">
            {pageCount} {pageCount === 1 ? 'Page' : 'Pages'}
          </span>
        </div>
      )}
      <div className="dna-row dna-sub">
        <span className="dna-icon">⏳</span>
        <span className="dna-value dna-muted">Waiting to process...</span>
      </div>
    </div>
  );
}
